import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Loader2, Save, Plus, Trash2, Download, ExternalLink, FileText, Mail } from "lucide-react";

const DOC = {
  som_po: { title: "PURCHASE ORDER", word: "Purchase Order", priced: true, remarksCol: "Lead Time", partyLabel: "Supplier (Bill From)" },
  som_dn: { title: "DELIVERY NOTE", word: "Delivery Note", priced: false, remarksCol: null, partyLabel: "Deliver To" },
  som_inv: { title: "TAX INVOICE", word: "Tax Invoice", priced: true, remarksCol: "Remarks", partyLabel: "Bill To" },
};
const DEFAULT_BAND = "450 Chole Road, Dar es Salaam, Tanzania  |  +255 687 719 909  |  info@somcoregroup.com  |  TIN: 193-286-429  |  VRN: 40-325861-W";
const num = (v) => { const n = Number(String(v ?? "").replace(/,/g, "")); return isNaN(n) ? 0 : n; };
const withCommas = (v) => { if (v === "" || v == null) return ""; const [i, d] = String(v).split("."); const fi = i.replace(/\B(?=(\d{3})+(?!\d))/g, ","); return d != null ? `${fi}.${d}` : fi; };

export default function OrderDocEdit() {
  const { docId } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState([]);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ to: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/docs/${docId}`);
      setDoc({ ...data, ref: data.ref || (data.context && data.context.supplier_quote_ref) || "" });
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [docId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/team-members").then((r) => setMembers(r.data)).catch(() => {}); }, []);

  const set = (patch) => setDoc((d) => ({ ...d, ...patch }));
  const setLine = (i, k, v) => setDoc((d) => ({ ...d, lines: d.lines.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)) }));
  const addLine = () => setDoc((d) => ({ ...d, lines: [...(d.lines || []), { description: "", part_no: "", quantity: 1, uom: "EA", unit_price: 0, lead_time: "", remarks: "" }] }));
  const rmLine = (i) => setDoc((d) => ({ ...d, lines: d.lines.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const lines = doc.lines.map((l) => ({ ...l, quantity: num(l.quantity), unit_price: num(l.unit_price) }));
      const { data } = await api.put(`/orders/docs/${docId}`, {
        ref: doc.ref, date: doc.date, currency: doc.currency, notes: doc.notes,
        payment_terms: doc.payment_terms, incoterms: doc.incoterms, delivery_date: doc.delivery_date,
        attention: doc.attention || (doc.context && doc.context.party && doc.context.party.attention) || "", prepared_by_id: doc.prepared_by_id, prepared_by_name: doc.prepared_by_name,
        vat_rate: doc.vat_rate, vat_add: doc.vat_add, lines,
      });
      setDoc((d) => ({ ...data, context: d.context })); toast.success("Document saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const download = async (format = "excel") => {
    try {
      const res = await api.get(`/orders/docs/${docId}/export`, { params: { format }, responseType: "blob" });
      const ext = format === "pdf" ? "pdf" : "xlsx";
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `${doc.filename}.${ext}`; a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiError(e)); }
  };
  const openEmail = () => {
    const p = (doc.context || {}).party || {};
    const by = (doc.context || {}).prepared_by || "Somcore Group Limited";
    const who = doc.attention || p.attention || p.name || "Sir/Madam";
    const word = (DOC[doc.doc_type] || {}).word || "document";
    let msg;
    if (doc.doc_type === "som_po")
      msg = `Dear ${who},\n\nI hope you are doing well.\n\nPlease find attached our Purchase Order ${doc.doc_no} for your reference and processing.\n\nKindly proceed with shipping the ex-stock items as soon as possible, while we await the remaining items on their quoted lead time.\n\nPlease do not hesitate to contact us should you have any questions.`;
    else if (doc.doc_type === "som_dn")
      msg = `Dear ${who},\n\nI hope you are doing well.\n\nPlease find attached our Delivery Note ${doc.doc_no} covering the goods delivered against your order.\n\nKindly acknowledge receipt by signing and stamping the delivery note.\n\nPlease do not hesitate to contact us should you have any questions.`;
    else
      msg = `Dear ${who},\n\nI hope you are doing well.\n\nPlease find attached our Tax Invoice ${doc.doc_no} for your kind attention and processing.\n\nPayment may be made to the bank details shown on the invoice — kindly quote the invoice number on your remittance.\n\nPlease do not hesitate to contact us should you have any questions.`;
    setEmailForm({ to: p.email || "", subject: `${word} ${doc.doc_no} | Somcore Group Limited`, message: msg });
    setEmailOpen(true);
  };
  const sendEmail = async () => {
    setSending(true);
    try { await api.post(`/orders/docs/${docId}/email`, emailForm); toast.success(`Emailed to ${emailForm.to}`); setEmailOpen(false); }
    catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  if (loading || !doc) return <AppLayout title="Document"><div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div></AppLayout>;

  const cfg = DOC[doc.doc_type] || DOC.som_po;
  const ctx = doc.context || {};
  const party = ctx.party || {};
  const contacts = party.contacts || [];
  const cur = (doc.currency || "TZS").toUpperCase();
  const money = (v) => `${cur} ${num(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const lines = doc.lines || [];
  const subtotal = lines.reduce((s, l) => s + num(l.quantity) * num(l.unit_price), 0);
  const totalQty = lines.reduce((s, l) => s + num(l.quantity), 0);
  const vatRate = doc.vat_rate != null ? num(doc.vat_rate) : 0.18;
  const vat = doc.vat_add ? subtotal * vatRate : 0;
  const grand = subtotal + vat;

  const ro = (v) => <span className="text-slate-700">{v || "—"}</span>;
  const dateInput = (val, key) => <Input type="date" className="h-8" value={(val || "").slice(0, 10)} onChange={(e) => set({ [key]: e.target.value })} data-testid={`doc-${key}`} />;
  const textInput = (val, key, ph) => <Input className="h-8" placeholder={ph} value={val || ""} onChange={(e) => set({ [key]: e.target.value })} data-testid={`doc-${key}`} />;
  const curInput = <Select value={cur} onValueChange={(v) => set({ currency: v })}><SelectTrigger className="h-8" data-testid="doc-currency"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="TZS">TZS</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent></Select>;
  const attentionNode = contacts.length ? (
    <Select value={doc.attention || party.attention || "none"} onValueChange={(v) => set({ attention: v === "none" ? "" : v })}>
      <SelectTrigger className="h-8" data-testid="doc-attention"><SelectValue placeholder="Select contact" /></SelectTrigger>
      <SelectContent><SelectItem value="none">—</SelectItem>{contacts.map((c, i) => <SelectItem key={i} value={c.name}>{c.name}{c.email ? ` · ${c.email}` : ""}</SelectItem>)}</SelectContent>
    </Select>
  ) : (
    <div>
      <Input className="h-8" placeholder="Type contact name" value={doc.attention || ""} onChange={(e) => set({ attention: e.target.value })} data-testid="doc-attention" />
      <div className="mt-1 text-[10px] text-muted-foreground">No CRM contacts for this {doc.doc_type === "som_po" ? "supplier" : "client"} yet — add them in the CRM to pick from a list.</div>
    </div>
  );
  const preparedNode = (
    <Select value={doc.prepared_by_id || "none"} onValueChange={(v) => { const m = members.find((x) => x.id === v); set({ prepared_by_id: v === "none" ? "" : v, prepared_by_name: v === "none" ? "" : (m?.name || "") }); }}>
      <SelectTrigger className="h-8" data-testid="doc-prepared-by"><SelectValue placeholder={ctx.prepared_by || "Select staff"} /></SelectTrigger>
      <SelectContent><SelectItem value="none">—</SelectItem>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}{m.job_title ? ` · ${m.job_title}` : ""}</SelectItem>)}</SelectContent>
    </Select>
  );

  let metaRows = [];
  if (doc.doc_type === "som_po") {
    metaRows = [
      [{ l: "PO Reference", n: <span className="font-mono font-semibold text-[#1B4383]">{doc.doc_no}</span> }, { l: "Date", n: dateInput(doc.date, "date") }],
      [{ l: cfg.partyLabel, n: <span className="font-semibold text-[#1B2A4A]">{party.name || "—"}</span> }, { l: "Supplier Quote No.", n: textInput(doc.ref, "ref", "Supplier quote ref") }],
      [{ l: "Attention", n: attentionNode }, { l: "Currency", n: curInput }],
      [{ l: "Supplier Email", n: ro(party.email) }, { l: "Payment Terms", n: textInput(doc.payment_terms, "payment_terms", "e.g. 30 Days") }],
      [{ l: "Supplier Phone", n: ro(party.phone) }, { l: "Incoterms", n: textInput(doc.incoterms, "incoterms", "e.g. Ex Works Dar es Salaam") }],
      [{ l: "Supplier TIN / VRN", n: ro(party.tin_vrn) }, { l: "Required Delivery Date", n: dateInput(doc.delivery_date, "delivery_date") }],
      [{ l: "Supplier Address", n: ro(party.address) }, { l: "Prepared By", n: preparedNode }],
    ];
  } else if (doc.doc_type === "som_dn") {
    metaRows = [
      [{ l: "Delivery Note No.", n: <span className="font-mono font-semibold text-[#1B4383]">{doc.doc_no}</span> }, { l: "Delivery Date", n: dateInput(doc.date, "date") }],
      [{ l: cfg.partyLabel, n: <span className="font-semibold text-[#1B2A4A]">{party.name || "—"}</span> }, { l: "Client PO Number", n: ro(ctx.client_po_ref) }],
      [{ l: "Attention", n: attentionNode }, { l: "Quote Reference", n: ro(ctx.quote_ref) }],
      [{ l: "Delivery Address", n: ro(party.address) }, { l: "Invoice Number", n: textInput(doc.ref, "ref", "Linked invoice no.") }],
      [{ l: "Client TIN / VRN", n: ro(party.tin_vrn) }, { l: "Issued By", n: preparedNode }],
    ];
  } else {
    metaRows = [
      [{ l: "Invoice Number", n: <span className="font-mono font-semibold text-[#1B4383]">{doc.doc_no}</span> }, { l: "Invoice Date", n: dateInput(doc.date, "date") }],
      [{ l: cfg.partyLabel, n: <span className="font-semibold text-[#1B2A4A]">{party.name || "—"}</span> }, { l: "Client PO Number", n: ro(ctx.client_po_ref) }],
      [{ l: "Attention", n: attentionNode }, { l: "Client PO Date", n: ro(ctx.client_po_date) }],
      [{ l: "Client Email", n: ro(party.email) }, { l: "Quote Reference", n: ro(ctx.quote_ref) }],
      [{ l: "Client Phone", n: ro(party.phone) }, { l: "Delivery Note No.", n: textInput(doc.ref, "ref", "Linked DN no.") }],
      [{ l: "Client TIN / VRN", n: ro(party.tin_vrn) }, { l: "Currency", n: curInput }],
      [{ l: "Client Address", n: ro(party.address) }, { l: "Payment Terms", n: textInput(doc.payment_terms, "payment_terms", "e.g. 30 Days") }],
    ];
  }

  const actions = (
    <div className="flex items-center gap-2">
      <Button variant="outline" onClick={openEmail} data-testid="doc-email"><Mail className="mr-2 h-4 w-4" />Email</Button>
      <Button variant="outline" onClick={() => download("pdf")} data-testid="doc-download-pdf"><Download className="mr-2 h-4 w-4" />PDF</Button>
      <Button variant="outline" onClick={() => download("excel")} data-testid="doc-download"><Download className="mr-2 h-4 w-4" />Excel</Button>
      <Button onClick={save} disabled={saving} data-testid="doc-save" className="bg-[#1B4383] hover:bg-[#16386e]">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save</Button>
    </div>
  );

  return (
    <AppLayout title={doc.doc_no} actions={actions}>
      <button onClick={() => nav(doc.order_id ? `/orders/${doc.order_id}` : "/orders")} className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-slate-800" data-testid="back-order"><ArrowLeft className="h-4 w-4" />Back to Order</button>

      <div className="mx-auto max-w-4xl space-y-4" style={{ fontFamily: "Calibri, Carlito, 'Segoe UI', sans-serif" }} data-testid="doc-editor">
        {doc.doc_type === "som_po" && doc.rfq_id && doc.supplier_id && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-white px-4 py-3" data-testid="doc-supplier-quote-card">
            <div className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4 text-[#1B4383]" />
              <span className="text-slate-700">Supplier quote &amp; costing for <b>{party.name || doc.supplier_name}</b></span>
            </div>
            <Button variant="outline" size="sm" onClick={() => nav(`/rfqs/${doc.rfq_id}/quotes/${doc.supplier_id}`)} data-testid="doc-supplier-quote-link">Open supplier quote<ExternalLink className="ml-1.5 h-3.5 w-3.5" /></Button>
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-border bg-white shadow-sm" data-testid="doc-paper">
          <div className="flex justify-center px-6 pt-6">
            <img src={`${process.env.PUBLIC_URL || ""}/somcore-logo.png`} alt="Somcore" className="h-12 w-auto" data-testid="doc-logo" />
          </div>
          <div className="mt-3 bg-[#1B4383] px-6 py-1.5 text-center text-[11px] text-white">{ctx.company_band || DEFAULT_BAND}</div>
          <div className="flex items-center justify-center gap-3 bg-[#BFBFBF]/50 px-6 py-2 text-center">
            <span className="font-heading text-base font-bold tracking-wide text-[#1F2937]">{cfg.title}</span>
            <span className="font-mono text-sm font-semibold text-[#1B4383]">{doc.doc_no}</span>
          </div>

          <div className="space-y-6 p-6">
            <div className="overflow-hidden rounded-lg border border-border" data-testid="doc-meta-grid">
              <table className="w-full text-sm"><tbody>
                {metaRows.map((mr, i) => (
                  <tr key={i} className="border-b border-border last:border-0">
                    <td className="w-[18%] bg-[#BFBFBF]/40 px-3 py-2 align-middle font-semibold text-[#1B2A4A]">{mr[0].l}</td>
                    <td className="w-[32%] px-3 py-1.5 align-middle">{mr[0].n}</td>
                    <td className="w-[18%] bg-[#BFBFBF]/40 px-3 py-2 align-middle font-semibold text-[#1B2A4A]">{mr[1].l}</td>
                    <td className="w-[32%] px-3 py-1.5 align-middle">{mr[1].n}</td>
                  </tr>
                ))}
              </tbody></table>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Line Items ({lines.length})</div>
                <Button size="sm" variant="outline" onClick={addLine} data-testid="doc-add-line"><Plus className="mr-1 h-3.5 w-3.5" />Add line</Button>
              </div>
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full table-fixed text-sm">
                  <thead className="bg-[#1B4383] text-left text-[11px] uppercase tracking-wide text-white">
                    <tr>
                      <th className="w-[5%] px-3 py-2.5 text-center font-semibold">S/N</th>
                      <th className={`${cfg.priced ? "w-[40%]" : "w-[80%]"} px-3 py-2.5 font-semibold`}>{doc.doc_type === "som_dn" ? "Description of Goods" : "Item Description"}</th>
                      <th className="w-[10%] px-3 py-2.5 text-center font-semibold">Quantity</th>
                      {cfg.priced && <th className="w-[15%] px-3 py-2.5 text-right font-semibold">Unit Price ({cur})</th>}
                      {cfg.priced && <th className="w-[16%] px-3 py-2.5 text-right font-semibold">Nett Value ({cur})</th>}
                      {cfg.remarksCol && <th className="w-[14%] px-3 py-2.5 font-semibold">{cfg.remarksCol}</th>}
                      <th className="w-[4%] px-2 py-2.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l, i) => (
                      <tr key={i} className="border-t border-border align-top" data-testid={`doc-line-${i}`}>
                        <td className="px-3 py-2 text-center font-mono text-xs text-muted-foreground">{i + 1}</td>
                        <td className="px-2 py-2">
                          <Input className="h-8 w-full" placeholder="Description" value={l.description || ""} onChange={(e) => setLine(i, "description", e.target.value)} data-testid={`doc-desc-${i}`} />
                          <Input className="mt-1 h-7 w-full text-xs" placeholder="Part No. / spec (optional)" value={l.part_no || ""} onChange={(e) => setLine(i, "part_no", e.target.value)} data-testid={`doc-part-${i}`} />
                        </td>
                        <td className="px-2 py-2 text-center"><Input type="number" className="h-8 w-full text-center font-mono" value={l.quantity ?? ""} onChange={(e) => setLine(i, "quantity", e.target.value)} data-testid={`doc-qty-${i}`} /></td>
                        {cfg.priced && <td className="px-2 py-2 text-right"><Input inputMode="decimal" className="h-8 w-full text-right font-mono" value={withCommas(l.unit_price)} onChange={(e) => setLine(i, "unit_price", e.target.value.replace(/,/g, ""))} data-testid={`doc-unit-${i}`} /></td>}
                        {cfg.priced && <td className="px-3 py-2 text-right font-mono font-semibold text-slate-900" data-testid={`doc-nett-${i}`}>{money(num(l.quantity) * num(l.unit_price))}</td>}
                        {cfg.remarksCol && <td className="px-2 py-2"><Input className="h-8 w-full text-xs" placeholder={cfg.remarksCol === "Lead Time" ? "Ex Stock" : ""} value={(cfg.remarksCol === "Lead Time" ? l.lead_time : l.remarks) || ""} onChange={(e) => setLine(i, cfg.remarksCol === "Lead Time" ? "lead_time" : "remarks", e.target.value)} data-testid={`doc-remarks-${i}`} /></td>}
                        <td className="px-2 py-2 text-center"><button className="text-red-500 hover:text-red-700" onClick={() => rmLine(i)} data-testid={`doc-rm-${i}`}><Trash2 className="mx-auto h-4 w-4" /></button></td>
                      </tr>
                    ))}
                    {!lines.length && <tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-muted-foreground">No lines. Click "Add line".</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end">
              {doc.doc_type === "som_dn" ? (
                <div className="flex w-full max-w-xs items-center justify-between rounded-md bg-[#0F172A] px-4 py-2.5 text-white" data-testid="doc-total-qty">
                  <span className="text-sm font-bold">Total Quantity</span><span className="font-mono text-base font-bold">{totalQty.toLocaleString()}</span>
                </div>
              ) : (
                <div className="w-full max-w-sm space-y-2">
                  <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Subtotal (Excl. VAT)</span><span className="font-mono font-semibold" data-testid="doc-subtotal">{money(subtotal)}</span></div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">VAT</span>
                      <Input type="number" min="0" max="100" step="0.01" className="h-7 w-16 text-right font-mono" value={Math.round(vatRate * 10000) / 100} onChange={(e) => set({ vat_rate: num(e.target.value) / 100 })} data-testid="doc-vat-rate" />
                      <span className="text-muted-foreground">%</span>
                      <label className="flex items-center gap-1.5 text-xs"><Checkbox checked={!!doc.vat_add} onCheckedChange={(v) => set({ vat_add: !!v })} data-testid="doc-vat-include" />include</label>
                    </div>
                    <span className="font-mono">{money(vat)}</span>
                  </div>
                  <div className="flex items-center justify-between rounded-md bg-[#0F172A] px-4 py-2.5 text-white"><span className="text-sm font-bold">TOTAL</span><span className="font-mono text-base font-bold" data-testid="doc-grand-total">{money(grand)}</span></div>
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Notes</label>
              <Textarea rows={2} value={doc.notes || ""} onChange={(e) => set({ notes: e.target.value })} data-testid="doc-notes" />
            </div>

            {doc.doc_type === "som_inv" && (
              <div className="overflow-hidden rounded-lg border border-border" data-testid="doc-bank">
                <div className="bg-[#1B4383] px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-white">Our Bank Details</div>
                <div className="p-3 text-[11px] leading-relaxed text-muted-foreground">
                  {(ctx.banks || []).length ? (ctx.banks).map((b, i) => (
                    <div key={i}>{b.bank_name} — A/C {b.account_name} · TZS {b.account_tzs} · USD {b.account_usd} · SWIFT {b.swift}</div>
                  )) : <span>Bank details print from Company Information → Company Profile.</span>}
                </div>
              </div>
            )}

            <div className="overflow-hidden rounded-lg border border-border">
              <div className="bg-[#1B4383] px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-white">
                {doc.doc_type === "som_po" ? "Terms & Conditions + Delivery / Invoicing Details" : doc.doc_type === "som_dn" ? "Delivery Conditions + Acknowledgement of Receipt" : "Invoice Notes + Declaration & Signatures"}
              </div>
              <div className="p-3 text-[11px] leading-relaxed text-muted-foreground">These sections (and everything after) print on <b>page 2</b> of the downloaded PDF — managed in Company Information.</div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-lg" data-testid="doc-email-dialog">
          <DialogHeader><DialogTitle>Email {doc.doc_no} {doc.doc_type === "som_po" ? "to Supplier" : "to Client"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><span className="text-xs text-muted-foreground">To</span><Input type="email" value={emailForm.to} onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })} data-testid="doc-email-to" /></div>
            <div className="space-y-1.5"><span className="text-xs text-muted-foreground">Subject</span><Input value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} data-testid="doc-email-subject" /></div>
            <div className="space-y-1.5"><span className="text-xs text-muted-foreground">Message</span><Textarea rows={10} value={emailForm.message} onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })} data-testid="doc-email-message" /></div>
            <p className="text-[11px] text-muted-foreground">The {cfg.word} PDF is attached as a secure download link.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button onClick={sendEmail} disabled={!emailForm.to || sending} data-testid="doc-email-send" className="bg-[#1B4383] hover:bg-[#16386e]">{sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
